import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Grid,
  Paper,
  alpha,
  useTheme,
  Button,
  Snackbar,
  Alert,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  IconButton,
  Chip
} from '@mui/material';
import {
  Receipt as InvoiceIcon,
  Save as SaveIcon,
  AutoAwesome as AutoFillIcon,
  Assignment as AssignmentIcon,
  Search as SearchIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { supabase } from '../../../../supabaseClient';
import { useParams, useNavigate } from 'react-router-dom';
import { usePermissions } from '../../../../utils/permissions';

const PaymentSection = ({ payment, setPayment, onSave }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { companyId, id: poId } = useParams();
  const { canViewInvoicePayment, canEditInvoicePayment } = usePermissions();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [saving, setSaving] = useState(false);
  
  // Invoice selection dialog states
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState('');
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  // Permission checks
  const canView = canViewInvoicePayment();
  const canEdit = canEditInvoicePayment();

  useEffect(() => {
    if (poId && poId !== 'new') {
      fetchPaymentData();
    }
  }, [poId]);

  const fetchPaymentData = async () => {
    try {
      const { data, error } = await supabase
        .from('po_delivery_confirmations')
        .select('invoice_sent, invoice_date, invoice_number, payment_collected, payment_amount, payment_method, additional_notes')
        .eq('purchase_order_id', poId)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // Ignore "not found" error
      
      if (data) {
        setPayment(prev => ({
          ...prev,
          invoiceSent: data.invoice_sent || '',
          invoiceDate: data.invoice_date || '',
          invoiceNumber: data.invoice_number || '',
          paymentCollected: data.payment_collected || '',
          paymentAmount: data.payment_amount ? String(data.payment_amount) : '',
          paymentMethod: data.payment_method || '',
          additionalNotes: data.additional_notes || ''
        }));
      } else {
        // No data found - set defaults
        setPayment(prev => ({
          ...prev,
          invoiceSent: 'no', // Default to "Not Yet Sent"
          invoiceDate: prev.invoiceDate || '',
          invoiceNumber: prev.invoiceNumber || '',
          paymentCollected: prev.paymentCollected || '',
          paymentAmount: prev.paymentAmount || '',
          paymentMethod: prev.paymentMethod || '',
          additionalNotes: prev.additionalNotes || ''
        }));
      }
    } catch (err) {
      console.error('Error fetching invoice and payment data:', err);
    }
  };

  const savePaymentData = async () => {
    setSaving(true);
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      // Check if record exists
      const { data: existing } = await supabase
        .from('po_delivery_confirmations')
        .select('id')
        .eq('purchase_order_id', poId)
        .single();

      const payload = {
        invoice_sent: payment.invoiceSent || null,
        invoice_date: payment.invoiceDate || null,
        invoice_number: payment.invoiceNumber || null,
        payment_collected: payment.paymentCollected || null,
        payment_amount: payment.paymentAmount ? parseFloat(payment.paymentAmount) : null,
        payment_method: payment.paymentMethod || null,
        additional_notes: payment.additionalNotes || null,
        post_delivery_completed: true,
        updated_at: new Date().toISOString()
      };

      if (existing) {
        // Update existing record
        const { error } = await supabase
          .from('po_delivery_confirmations')
          .update(payload)
          .eq('purchase_order_id', poId);

        if (error) throw error;
      } else {
        // Create new record
        const { error } = await supabase
          .from('po_delivery_confirmations')
          .insert({
            company_id: companyId,
            purchase_order_id: poId,
            created_by: user.id,
            ...payload
          });

        if (error) throw error;
      }

      setSnackbar({ open: true, message: 'Invoice and payment information saved successfully', severity: 'success' });
      
      // Reload completion flags
      if (onSave) onSave();
    } catch (err) {
      console.error('Error saving invoice and payment data:', err);
      setSnackbar({ open: true, message: 'Failed to save invoice and payment information', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setPayment({ ...payment, [field]: value });
  };

  const handleAutofillAmount = async () => {
    try {
      const { data, error } = await supabase
        .from('po_creation')
        .select('amount_due, payment_method')
        .eq('purchase_order_id', poId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        // Update both fields at once to avoid state update issues
        setPayment(prev => ({
          ...prev,
          ...(data.amount_due && { paymentAmount: String(data.amount_due) }),
          ...(data.payment_method && { paymentMethod: data.payment_method })
        }));
        
        const filled = [];
        if (data.amount_due) filled.push('amount');
        if (data.payment_method) filled.push('payment method');
        
        if (filled.length > 0) {
          setSnackbar({ open: true, message: `Autofilled ${filled.join(' and ')} from PO`, severity: 'success' });
        } else {
          setSnackbar({ open: true, message: 'No payment information found in PO', severity: 'info' });
        }
      } else {
        setSnackbar({ open: true, message: 'No PO found', severity: 'info' });
      }
    } catch (err) {
      console.error('Error fetching PO payment info:', err);
      setSnackbar({ open: true, message: 'Failed to autofill payment information', severity: 'error' });
    }
  };

  const fetchInvoices = async () => {
    setLoadingInvoices(true);
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          id,
          invoice_number,
          invoice_date,
          due_date,
          status,
          total_amount,
          amount_due,
          customer_id,
          customers (
            name,
            company
          )
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setInvoices(data || []);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setSnackbar({ open: true, message: 'Failed to load invoices', severity: 'error' });
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleOpenInvoiceDialog = () => {
    fetchInvoices();
    setInvoiceDialogOpen(true);
  };

  const handleCloseInvoiceDialog = () => {
    setInvoiceDialogOpen(false);
    setInvoiceSearchTerm('');
  };

  const handleSelectInvoice = (invoice) => {
    setPayment(prev => ({
      ...prev,
      invoiceNumber: invoice.invoice_number,
      invoiceDate: invoice.invoice_date
    }));
    setInvoiceDialogOpen(false);
    setInvoiceSearchTerm('');
    setSnackbar({ open: true, message: `Invoice ${invoice.invoice_number} selected`, severity: 'success' });
  };

  const getFilteredInvoices = () => {
    if (!invoiceSearchTerm.trim()) return invoices;
    
    const term = invoiceSearchTerm.toLowerCase();
    return invoices.filter(invoice => 
      invoice.invoice_number?.toLowerCase().includes(term) ||
      invoice.customers?.name?.toLowerCase().includes(term) ||
      invoice.customers?.company?.toLowerCase().includes(term) ||
      invoice.status?.toLowerCase().includes(term)
    );
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  // Access Denied Card
  if (!canView) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Invoice & Payment Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Invoice & Payment. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Card sx={{
      mb: 3,
      borderRadius: 3,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 20px ${alpha(theme.palette.grey[500], 0.15)}`
      }
    }}>
      <CardContent sx={{ p: 4 }}>
        {/* Section Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              p: 1.5,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
            }}>
              <InvoiceIcon sx={{ fontSize: 28, color: 'success.main' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 500, color: 'success.main', mb: 0.5 }}>
                Invoice & Payment
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage invoice and payment information
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => canEdit && savePaymentData()}
            disabled={saving || !canEdit}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              opacity: canEdit ? 1 : 0.3,
              pointerEvents: canEdit ? 'auto' : 'none',
              cursor: canEdit ? 'pointer' : 'not-allowed'
            }}
          >
            {saving ? 'Saving...' : 'Save Invoice & Payment'}
          </Button>
        </Box>

        <Grid container spacing={3}>
          {/* Invoice Sent */}
          <Grid item xs={12}>
            <Paper sx={{
              p: 2.5,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.grey[50], 0.5),
              opacity: canEdit ? 1 : 0.6
            }}>
              <FormControl component="fieldset" fullWidth disabled={!canEdit}>
                <FormLabel sx={{ fontWeight: 500, color: 'text.primary', mb: 1.5 }}>
                  Invoice Sent to Customer?
                </FormLabel>
                <RadioGroup
                  value={payment.invoiceSent || ''}
                  onChange={(e) => canEdit && handleChange('invoiceSent', e.target.value)}
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes, Invoice Sent" sx={{ mb: 1 }} />
                  <FormControlLabel value="no" control={<Radio />} label="Not Yet Sent" sx={{ mb: 1 }} />
                  <FormControlLabel value="pending" control={<Radio />} label="Pending" />
                </RadioGroup>
              </FormControl>
            </Paper>
          </Grid>

          {/* Create Invoice Button - shows when "Not Yet Sent" is selected */}
          {payment.invoiceSent === 'no' && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<InvoiceIcon />}
                  onClick={() => {
                    const invoiceUrl = `/company/${companyId}/invoices/new`;
                    window.open(invoiceUrl, '_blank', 'width=1400,height=900');
                  }}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 4,
                    py: 1.5,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.35)}`
                    }
                  }}
                >
                  Create Invoice
                </Button>
              </Box>
            </Grid>
          )}

          {/* Invoice Date */}
          {payment.invoiceSent === 'yes' && (
            <>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Select an existing invoice or enter manually
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SearchIcon />}
                    onClick={handleOpenInvoiceDialog}
                    disabled={!canEdit}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 500,
                      opacity: canEdit ? 1 : 0.3,
                      pointerEvents: canEdit ? 'auto' : 'none',
                      cursor: canEdit ? 'pointer' : 'not-allowed',
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: alpha(theme.palette.primary.main, 0.05)
                      }
                    }}
                  >
                    Select from Invoices
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Invoice Date"
                    value={payment.invoiceDate ? dayjs(payment.invoiceDate) : null}
                    onChange={(newValue) => canEdit && handleChange('invoiceDate', newValue ? newValue.format('YYYY-MM-DD') : '')}
                    disabled={!canEdit}
                    sx={{ 
                      width: '100%',
                      opacity: canEdit ? 1 : 0.6,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        '& fieldset': { border: 'none' }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Invoice Number"
                  value={payment.invoiceNumber || ''}
                  onChange={(e) => canEdit && handleChange('invoiceNumber', e.target.value)}
                  disabled={!canEdit}
                  placeholder="INV-XXXXX"
                  sx={{
                    opacity: canEdit ? 1 : 0.6,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
            </>
          )}

          {/* Payment Collected (if applicable) */}
          <Grid item xs={12}>
            <Paper sx={{
              p: 2.5,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.grey[50], 0.5),
              opacity: canEdit ? 1 : 0.6
            }}>
              <FormControl component="fieldset" fullWidth disabled={!canEdit}>
                <FormLabel sx={{ fontWeight: 500, color: 'text.primary', mb: 1.5 }}>
                  Payment Collected on Delivery? (COD)
                </FormLabel>
                <RadioGroup
                  value={payment.paymentCollected || ''}
                  onChange={(e) => canEdit && handleChange('paymentCollected', e.target.value)}
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes, Collected" sx={{ mb: 1 }} />
                  <FormControlLabel value="no" control={<Radio />} label="No / Not COD" sx={{ mb: 1 }} />
                  <FormControlLabel value="partial" control={<Radio />} label="Partial Payment" />
                </RadioGroup>
              </FormControl>
            </Paper>
          </Grid>

          {/* Payment Details */}
          {(payment.paymentCollected === 'yes' || payment.paymentCollected === 'partial') && (
            <>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AutoFillIcon />}
                    onClick={() => canEdit && handleAutofillAmount()}
                    disabled={!canEdit}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 500,
                      opacity: canEdit ? 1 : 0.3,
                      pointerEvents: canEdit ? 'auto' : 'none',
                      cursor: canEdit ? 'pointer' : 'not-allowed',
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: alpha(theme.palette.primary.main, 0.05)
                      }
                    }}
                  >
                    Autofill from PO
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Payment Amount"
                  type="number"
                  value={payment.paymentAmount || ''}
                  onChange={(e) => canEdit && handleChange('paymentAmount', e.target.value)}
                  disabled={!canEdit}
                  placeholder="0.00"
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                  }}
                  sx={{
                    opacity: canEdit ? 1 : 0.6,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Payment Method"
                  value={payment.paymentMethod || ''}
                  onChange={(e) => canEdit && handleChange('paymentMethod', e.target.value)}
                  disabled={!canEdit}
                  placeholder="Cash, Check, Card, etc."
                  sx={{
                    opacity: canEdit ? 1 : 0.6,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
            </>
          )}

          {/* Additional Notes */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Additional Notes"
              multiline
              rows={3}
              value={payment.additionalNotes || ''}
              onChange={(e) => canEdit && handleChange('additionalNotes', e.target.value)}
              disabled={!canEdit}
              placeholder="Any other relevant information about the invoice or payment..."
              sx={{
                opacity: canEdit ? 1 : 0.6,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
          </Grid>
        </Grid>
      </CardContent>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Invoice Selection Dialog */}
      <Dialog
        open={invoiceDialogOpen}
        onClose={handleCloseInvoiceDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <InvoiceIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Select Invoice
            </Typography>
          </Box>
          <IconButton onClick={handleCloseInvoiceDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* Search Bar */}
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.2)}` }}>
            <TextField
              fullWidth
              placeholder="Search by invoice number, customer name, or status..."
              value={invoiceSearchTerm}
              onChange={(e) => setInvoiceSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 2
                }
              }}
            />
          </Box>

          {/* Invoices Table */}
          <TableContainer sx={{ maxHeight: 450 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.grey[100], 0.5) }}>
                    Invoice Number
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.grey[100], 0.5) }}>
                    Customer
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.grey[100], 0.5) }}>
                    Invoice Date
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.grey[100], 0.5) }}>
                    Due Date
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.grey[100], 0.5) }}>
                    Status
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.grey[100], 0.5) }}>
                    Total Amount
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.grey[100], 0.5) }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingInvoices ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <Typography variant="body2" color="text.secondary">
                        Loading invoices...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : getFilteredInvoices().length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <Typography variant="body2" color="text.secondary">
                        {invoiceSearchTerm ? 'No invoices match your search' : 'No invoices found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  getFilteredInvoices().map((invoice) => (
                    <TableRow 
                      key={invoice.id}
                      hover
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.05)
                        }
                      }}
                      onClick={() => handleSelectInvoice(invoice)}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {invoice.invoice_number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {invoice.customers?.name || '—'}
                        </Typography>
                        {invoice.customers?.company && (
                          <Typography variant="caption" color="text.secondary">
                            {invoice.customers.company}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={invoice.status || 'Unknown'} 
                          color={getStatusColor(invoice.status)}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          ${parseFloat(invoice.total_amount || 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectInvoice(invoice);
                          }}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500
                          }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.3)}` }}>
          <Button 
            onClick={handleCloseInvoiceDialog}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default PaymentSection;